import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';

// Boost (2026-10-12): members shown first in everyone's Discover row, until each
// viewer has seen them all. Add by name search, remove from the list.
const btn = { borderRadius: 10, padding: '9px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const card = { display: 'flex', gap: 14, alignItems: 'center', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 12, flexWrap: 'wrap' };
const thumb = { width: 56, height: 56, borderRadius: 12, objectFit: 'cover', background: 'rgba(255,255,255,0.05)' };
const muted = { color: 'rgba(255,255,255,.45)', fontSize: 13 };
const primary = { ...btn, border: 'none', background: 'linear-gradient(135deg,#0A3FD6,#7229F5)', color: '#fff' };
const ghost = { ...btn, background: 'transparent', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.7)' };

const Boost = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [showing, setShowing] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const latestSearch = useRef(0);

  const load = async () => {
    try {
      const res = await api.get('/users/admin/boosted');
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setShowing(res.data?.showing || 0);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load the Boost list. Refresh to try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Debounced name search over the same admin Users list. Only the newest
  // search may fill the results.
  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { ++latestSearch.current; setResults([]); setSearching(false); return undefined; }
    setSearching(true);
    const n = ++latestSearch.current;
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/users', { params: { search: q, records: 8, page: 1 } });
        if (n === latestSearch.current) setResults(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch {
        if (n === latestSearch.current) setResults([]);
      } finally {
        if (n === latestSearch.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [term]);

  const setBoost = async (userId, boosted) => {
    setBusyId(String(userId));
    try {
      await api.post(`/users/admin/boosted/${userId}`, { boosted });
      await load();
    } catch (e) {
      alert(e?.response?.data?.message || 'Could not update. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = (r) => {
    if (!window.confirm(`Remove ${r.name || 'this member'} from Boost?`)) return;
    setBoost(r._id, false);
  };

  const boostedIds = new Set(rows.map((r) => String(r._id)));
  const women = rows.filter((r) => r.showing && /^f/i.test(r.gender || '')).length;

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section style={{ padding: '20px 2vw', width: '100%', maxWidth: 1100, boxSizing: 'border-box' }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>Boost</h1>
            <p style={{ ...muted, margin: 0 }}>
              Boosted members show first in every member's Discover row, 5 a day (2 women, 3 others), until that member has seen them all. Someone you add later shows to everyone, including people who already saw the rest.
            </p>

            <div style={{ margin: '20px 0 10px' }}>
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Search members by name or email to boost"
                style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 12, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: 14, outline: 'none' }}
              />
            </div>
            {searching && <p style={muted}>Searching…</p>}
            {!searching && term.trim().length >= 2 && results.length === 0 && <p style={muted}>No members match.</p>}
            {results.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {results.map((u) => {
                  const on = boostedIds.has(String(u._id));
                  const busy = busyId === String(u._id);
                  return (
                    <div key={u._id} style={card}>
                      <img src={u.photo_url || ''} alt="" style={thumb} />
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unnamed'}</div>
                        <div style={{ ...muted, fontSize: 12 }}>{[u.gender, u.email].filter(Boolean).join(' · ')}</div>
                        {!u.photo_url && <div style={{ color: '#f0a13c', fontSize: 12, marginTop: 2 }}>No profile photo, so the row can't show them.</div>}
                      </div>
                      {on
                        ? <span style={{ ...muted, fontSize: 12 }}>Boosted</span>
                        : <button type="button" disabled={busy} onClick={() => setBoost(u._id, true)} style={primary}>{busy ? '…' : 'Boost'}</button>}
                    </div>
                  );
                })}
              </div>
            )}

            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '24px 0 4px' }}>
              Boosted · {rows.length}
            </h2>
            <p style={{ ...muted, margin: '0 0 14px' }}>{showing} can show in the row right now, {women} of them women.</p>

            {loading && <p style={muted}>Loading…</p>}
            {!loading && error && (
              <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 14, padding: 20, color: '#f87171' }}>{error}</div>
            )}
            {!loading && !error && rows.length === 0 && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 40, textAlign: 'center', color: 'rgba(255,255,255,.4)' }}>
                No one is boosted. Search above to add someone.
              </div>
            )}
            {!loading && !error && rows.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rows.map((r) => {
                  const busy = busyId === String(r._id);
                  return (
                    <div key={r._id} style={card}>
                      <img src={r.photo_url || ''} alt="" style={thumb} />
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{r.name || 'Unnamed'}</div>
                        <div style={{ fontSize: 12, marginTop: 2, color: r.showing ? 'rgba(255,255,255,.45)' : '#f0a13c' }}>
                          {[r.gender, r.showing ? 'Showing' : r.reason].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <button type="button" onClick={() => navigate(`/users/edit/${r.id || r._id}`)} style={ghost}>View profile</button>
                      <button type="button" disabled={busy} onClick={() => remove(r)} style={{ ...ghost, border: '1px solid rgba(248,113,113,.4)', color: '#f87171' }}>{busy ? '…' : 'Remove'}</button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default Boost;
